import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import config from "../config";
import {
  OfflineCacheManager,
  OfflineQueueManager,
  OfflineEntityMap,
  OfflineMutationProcessor,
  OfflineProcessorResult,
  OfflineQueueItem,
  OfflineQueueState,
  OfflineMutationType,
  ItemStatusMutationPayload,
  decorateEntities,
  OfflineSyncInfo,
  getPersistentStorage,
  PersistentStorage,
} from "../offline";
import { OfflineQueueStatus } from "../offline/queue/types";

type ProcessorRegistry = Map<OfflineMutationType, Set<OfflineMutationProcessor>>;

interface OfflineSyncContextValue {
  featureEnabled: boolean;
  isReady: boolean;
  isOnline: boolean;
  isForcedOffline: boolean;
  toggleForcedOffline: () => Promise<void>;
  setForcedOffline: (value: boolean) => Promise<void>;
  queueState: OfflineQueueState | null;
  queueCounts: {
    total: number;
    pending: number;
    syncing: number;
    error: number;
  };
  metadata: OfflineEntityMap;
  pausedReason?: string;
  enqueueItemStatus: (payload: ItemStatusMutationPayload) => Promise<void>;
  registerProcessor: (
    type: OfflineMutationType,
    processor: OfflineMutationProcessor
  ) => () => void;
  flushQueue: () => Promise<void>;
  syncNow: () => Promise<void>;
  resumeSync: () => Promise<void>;
  decorateItems: <T extends { id: string }>(
    items: T[],
    entityType: "item" | "order"
  ) => Array<T & { offline?: OfflineSyncInfo }>;
  storage: PersistentStorage | null;
  cacheManager: OfflineCacheManager | null;
  queueManager: OfflineQueueManager | null;
}

const OfflineSyncContext = createContext<OfflineSyncContextValue>({
  featureEnabled: config.offlineSyncEnabled,
  isReady: false,
  isOnline: true,
  isForcedOffline: false,
  queueState: null,
  queueCounts: { total: 0, pending: 0, syncing: 0, error: 0 },
  metadata: {},
  enqueueItemStatus: async () => {},
  registerProcessor: () => () => {},
  flushQueue: async () => {},
  syncNow: async () => {},
  resumeSync: async () => {},
  toggleForcedOffline: async () => {},
  setForcedOffline: async () => {},
  pausedReason: undefined,
  decorateItems: (items) => items,
  storage: null,
  cacheManager: null,
  queueManager: null,
});

const FORCED_OFFLINE_KEY = "offline:forced-offline";

export const OfflineSyncProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [storage, setStorage] = useState<PersistentStorage | null>(null);
  const [queueManager, setQueueManager] = useState<OfflineQueueManager | null>(
    null
  );
  const [cacheManager, setCacheManager] = useState<OfflineCacheManager | null>(
    null
  );
  const [queueState, setQueueState] = useState<OfflineQueueState | null>(null);
  const [forcedOffline, setForcedOfflineState] = useState<boolean>(false);
  const [networkOnline, setNetworkOnline] = useState<boolean>(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  const processorsRef = useRef<ProcessorRegistry>(new Map());
  const flushingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    getPersistentStorage()
      .then((adapter) => {
        if (!cancelled) {
          setStorage(adapter);
        }
      })
      .catch((error) => {
        console.error("[OfflineSync] Failed to initialize storage", error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!storage) return;

    const manager = new OfflineQueueManager(storage);
    const cache = new OfflineCacheManager(storage);
    setQueueManager(manager);
    setCacheManager(cache);

    const unsubscribe = manager.subscribe((state) => {
      setQueueState({ ...state });
    });

    storage
      .getItem<boolean>(FORCED_OFFLINE_KEY)
      .then((value) => {
        if (typeof value === "boolean") {
          setForcedOfflineState(value);
        }
      })
      .catch((error) =>
        console.warn("[OfflineSync] Failed to read forced offline state", error)
      );

    return () => {
      unsubscribe();
    };
  }, [storage]);

  useEffect(() => {
    const handleOnline = () => setNetworkOnline(true);
    const handleOffline = () => setNetworkOnline(false);
    if (typeof window !== "undefined") {
      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
      }
    };
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    if (typeof window === "undefined") {
      return;
    }
    import("@capacitor/network")
      .then(({ Network }) => {
        Network.getStatus()
          .then((status) => setNetworkOnline(status.connected))
          .catch((error) =>
            console.warn("[OfflineSync] Unable to get initial network status", error)
          );
        const listener = Network.addListener("networkStatusChange", (status) => {
          setNetworkOnline(status.connected);
        });
        unsubscribe = () => {
          listener
            .then((handle) => handle.remove())
            .catch((error) =>
              console.warn("[OfflineSync] Failed to remove network listener", error)
            );
        };
      })
      .catch((error) => {
        console.debug(
          "[OfflineSync] Capacitor Network plugin unavailable, relying on navigator.onLine",
          error
        );
      });
    return () => {
      unsubscribe?.();
    };
  }, []);

  const setForcedOffline = useCallback(
    async (value: boolean) => {
      setForcedOfflineState(value);
      if (storage) {
        await storage.setItem(FORCED_OFFLINE_KEY, value);
      }
    },
    [storage]
  );

  const toggleForcedOffline = useCallback(async () => {
    await setForcedOffline(!forcedOffline);
  }, [forcedOffline, setForcedOffline]);

  const featureEnabled = config.offlineSyncEnabled;
  const isReady = Boolean(storage && queueManager && cacheManager);
  const isOnline = featureEnabled && !forcedOffline && networkOnline;

  const queueCounts = useMemo(() => {
    if (!queueState) {
      return { total: 0, pending: 0, syncing: 0, error: 0 };
    }

    const counts = {
      total: queueState.items.length,
      pending: 0,
      syncing: 0,
      error: 0,
    };

    queueState.items.forEach((item) => {
      if (item.metadata.status === "pending") counts.pending += 1;
      if (item.metadata.status === "syncing") counts.syncing += 1;
      if (item.metadata.status === "error") counts.error += 1;
    });
    return counts;
  }, [queueState]);

  const getProcessor = useCallback(
    (type: OfflineMutationType): OfflineMutationProcessor | null => {
      const registry = processorsRef.current.get(type);
      if (!registry || registry.size === 0) return null;
      const [first] = registry;
      return first ?? null;
    },
    []
  );

  const processItem = useCallback(
    async (item: OfflineQueueItem): Promise<OfflineProcessorResult> => {
      const processor = getProcessor(item.type);
      if (!processor) {
        return {
          ok: false,
          retry: false,
          errorMessage: `No processor registered for ${item.type}`,
        };
      }
      return processor(item);
    },
    [getProcessor]
  );

  const flushQueue = useCallback(async () => {
    if (!queueManager || !isReady || flushingRef.current) {
      if (process.env.NODE_ENV !== "production") {
        console.debug("[OfflineSync] flushQueue skipped", {
          hasQueueManager: Boolean(queueManager),
          isReady,
          flushing: flushingRef.current,
        });
      }
      return;
    }
    const state = queueState ?? (await queueManager.getState());
    if (process.env.NODE_ENV !== "production") {
      console.log("[OfflineSync] flushQueue invoked", {
        itemCount: state.items.length,
        paused: state.paused,
        pausedReason: state.pausedReason,
        isOnline,
      });
    }
    if (state.paused) {
      return;
    }
    if (!isOnline) {
      if (process.env.NODE_ENV !== "production") {
        console.log("[OfflineSync] flushQueue aborted: offline");
      }
      return;
    }
    flushingRef.current = true;
    try {
      for (const item of [...state.items]) {
        if (!isOnline) {
          break;
        }
        if (process.env.NODE_ENV !== "production") {
          console.log("[OfflineSync] Processing queue item", {
            id: item.id,
            type: item.type,
            status: item.metadata.status,
            attempts: item.metadata.attempts,
          });
        }
        await queueManager.markItemSyncing(item.id);
        try {
          const result = await processItem(item);
          if (process.env.NODE_ENV !== "production") {
            console.log("[OfflineSync] Processor result", {
              id: item.id,
              ok: result.ok,
              retry: "retry" in result ? result.retry : undefined,
              pause: "pause" in result ? result.pause : undefined,
              errorMessage: result.errorMessage,
            });
          }
          if (result.ok) {
            await queueManager.markItemSynced(item.id, result.lastSyncAt);
            continue;
          }
          if ("pause" in result && result.pause) {
            await queueManager.markItemFailed(
              item.id,
              result.errorMessage ?? "Sync paused"
            );
            await queueManager.setPaused(true, result.pauseReason ?? result.errorMessage);
            break;
          }
          if (result.retry) {
            await queueManager.markItemPending(item.id, result.errorMessage);
            break;
          }
          await queueManager.markItemFailed(
            item.id,
            result.errorMessage ?? "Unknown sync error"
          );
          break;
        } catch (error) {
          const message =
            error instanceof Error ? error.message : String(error);
          console.error("[OfflineSync] Processor threw", {
            id: item.id,
            error,
          });
          await queueManager.markItemPending(item.id, message);
          break;
        }
      }
    } finally {
      flushingRef.current = false;
    }
  }, [isOnline, isReady, processItem, queueManager, queueState]);

  const enqueueItemStatus = useCallback(
    async (payload: ItemStatusMutationPayload) => {
      if (!featureEnabled || !queueManager) {
        return;
      }
      await queueManager.enqueueItemStatusMutation(payload);
      if (isOnline) {
        void flushQueue();
      }
    },
    [featureEnabled, flushQueue, isOnline, queueManager]
  );

  const registerProcessor = useCallback<
    OfflineSyncContextValue["registerProcessor"]
  >(
    (type, processor) => {
      if (!processorsRef.current.has(type)) {
        processorsRef.current.set(type, new Set());
      }
      const registry = processorsRef.current.get(type)!;
      registry.add(processor);
      if (isOnline) {
        void flushQueue();
      }
      return () => {
        registry.delete(processor);
        if (registry.size === 0) {
          processorsRef.current.delete(type);
        }
      };
    },
    [flushQueue, isOnline]
  );

  const syncNow = useCallback(async () => {
    if (!queueManager) return;
    await queueManager.setPaused(false);
    await flushQueue();
  }, [flushQueue, queueManager]);

  const resumeSync = useCallback(async () => {
    if (!queueManager) return;
    await queueManager.setPaused(false);
    if (isOnline) {
      await flushQueue();
    }
  }, [flushQueue, isOnline, queueManager]);

  useEffect(() => {
    if (!queueManager) return;
    if (isOnline && queueState?.items.length && !queueState.paused) {
      void flushQueue();
    }
  }, [flushQueue, isOnline, queueManager, queueState]);

  const decorateItems = useCallback<
    OfflineSyncContextValue["decorateItems"]
  >(
    (items, entityType) => {
      if (!queueState) return items;
      return decorateEntities(items, queueState.entityMetadata, entityType);
    },
    [queueState]
  );

  const value = useMemo<OfflineSyncContextValue>(
    () => ({
      featureEnabled,
      isReady,
      isOnline,
      isForcedOffline: forcedOffline,
      toggleForcedOffline,
      setForcedOffline,
      queueState,
      queueCounts,
      metadata: queueState?.entityMetadata ?? {},
      pausedReason: queueState?.pausedReason,
      enqueueItemStatus,
      registerProcessor,
      flushQueue,
      syncNow,
      resumeSync,
      decorateItems,
      storage,
      cacheManager,
      queueManager,
    }),
    [
      cacheManager,
      decorateItems,
      enqueueItemStatus,
      featureEnabled,
      flushQueue,
      forcedOffline,
      isOnline,
      isReady,
      queueCounts,
      queueManager,
      queueState,
      registerProcessor,
      resumeSync,
      setForcedOffline,
      storage,
      syncNow,
      toggleForcedOffline,
    ]
  );

  return (
    <OfflineSyncContext.Provider value={value}>
      {children}
    </OfflineSyncContext.Provider>
  );
};

export const useOfflineSync = (): OfflineSyncContextValue =>
  useContext(OfflineSyncContext);

export const useOfflineStatusBadge = (
  status: OfflineQueueStatus | undefined
): { color: "success" | "warning" | "error"; label: string } | null => {
  if (!status) return null;
  switch (status) {
    case "pending":
    case "syncing":
      return { color: "warning", label: "Pending Sync" };
    case "error":
      return { color: "error", label: "Sync Error" };
    default:
      return { color: "success", label: "Synced" };
  }
};
